/**
 * Save slot and title-screen view-models (spec §10, §11; plan §6 M2-18): the slot rows of the Saves tab and the
 * Load screen (corrupt and future slots named with their error, never overwritten silently), Continue (the newest
 * valid save), the "load the newest valid autosave instead" fallback, and the deferred-asset progress.
 */
import { stationIndexOf } from '../../data/m2'
import type { M2Data } from '../../data/m2Schemas'
import { slotLabel } from '../../save/slots'
import { STORAGE_BANNER } from '../../save/storage'
import { AUTO_SLOTS, MANUAL_SLOTS, SAVE_SLOTS, type SaveSlotId, type SaveSlotInfo, type UiState } from '../contracts'
import { formatCr, formatDuration, formatSavedAt } from '../format'

export interface SaveRow {
  slot: SaveSlotId
  label: string
  manual: boolean
  status: SaveSlotInfo['status']
  /** "Charter Hub · 1 h 05 min of flight · 12,400 cr · lease 180,000 cr", "Empty", or the problem. */
  summary: string
  savedAt: string
  error: string | null
  canLoad: boolean
  /** Non-empty slots export as they are (a corrupt save exports its raw text). */
  canExport: boolean
  canSave: boolean
}

function stationName(data: Pick<M2Data, 'stations'>, id: string | undefined): string | null {
  if (id === undefined) return null
  const i = stationIndexOf(data, id)
  return i < 0 ? id : data.stations.stations[i].name
}

function summaryOf(data: Pick<M2Data, 'stations'>, info: SaveSlotInfo): string {
  switch (info.status) {
    case 'empty':
      return 'Empty'
    case 'corrupt':
      return 'Corrupt save'
    case 'future':
      return 'Saved by a newer version'
    case 'ok': {
      const parts: string[] = []
      const where = stationName(data, info.station)
      if (where !== null) parts.push(where)
      if (info.gameTime !== undefined) parts.push(`${formatDuration(info.gameTime)} of flight`)
      if (info.wallet !== undefined) parts.push(formatCr(info.wallet))
      if (info.principal !== undefined) parts.push(info.principal > 0 ? `lease ${formatCr(info.principal)}` : 'owned')
      return parts.join(' · ')
    }
  }
}

/** Slot infos in `SAVE_SLOTS` order, a missing slot reading as empty. */
export function orderedSlots(saves: readonly SaveSlotInfo[]): SaveSlotInfo[] {
  return SAVE_SLOTS.map((slot) => saves.find((s) => s.slot === slot) ?? { slot, status: 'empty' as const })
}

export function saveRow(data: Pick<M2Data, 'stations'>, info: SaveSlotInfo, canSaveNow: boolean): SaveRow {
  const manual = (MANUAL_SLOTS as readonly string[]).includes(info.slot)
  return {
    slot: info.slot,
    label: slotLabel(info.slot),
    manual,
    status: info.status,
    summary: summaryOf(data, info),
    savedAt: formatSavedAt(info.savedAt),
    error: info.status === 'corrupt' || info.status === 'future' ? (info.error ?? 'This save cannot be read.') : null,
    canLoad: info.status === 'ok',
    canExport: info.status !== 'empty',
    canSave: manual && canSaveNow,
  }
}

export interface SavesModel {
  manual: SaveRow[]
  auto: SaveRow[]
  storageBanner: string | null
}

/** The Saves tab: manual slots (save, load, export), autosaves (load, export). */
export function savesModel(data: Pick<M2Data, 'stations'>, state: Pick<UiState, 'saves' | 'storage' | 'career'>): SavesModel {
  const rows = orderedSlots(state.saves).map((s) => saveRow(data, s, state.career !== null && state.storage !== 'none'))
  return {
    manual: rows.filter((r) => r.manual),
    auto: rows.filter((r) => !r.manual),
    storageBanner: state.storage === 'indexeddb' ? null : STORAGE_BANNER,
  }
}

/** The newest valid save by `savedAt`, then game time (among `among` slots when given). */
export function newestValid(saves: readonly SaveSlotInfo[], among: readonly SaveSlotId[] = SAVE_SLOTS): SaveSlotInfo | null {
  let best: SaveSlotInfo | null = null
  for (const s of saves) {
    if (s.status !== 'ok' || !among.includes(s.slot)) continue
    if (best === null) {
      best = s
      continue
    }
    const a = s.savedAt ?? ''
    const b = best.savedAt ?? ''
    if (a > b || (a === b && (s.gameTime ?? 0) > (best.gameTime ?? 0))) best = s
  }
  return best
}

export interface TitleModel {
  /** Continue: the newest valid save, or null (the button is disabled). */
  continueSlot: SaveSlotId | null
  continueLabel: string
  continueDetail: string
  /** Any slot holds something (New career asks for a confirm). */
  hasSaves: boolean
  rows: SaveRow[]
  /** A slot is corrupt or from a newer build. */
  anyBad: boolean
  /** "Load the newest valid autosave instead" target (null: no valid autosave). */
  fallback: SaveRow | null
  assetsReady: boolean
  assetsLabel: string
  storageBanner: string | null
}

export function titleModel(data: Pick<M2Data, 'stations'>, state: Pick<UiState, 'saves' | 'storage' | 'assetsReady' | 'assetsProgress'>): TitleModel {
  const slots = orderedSlots(state.saves)
  const rows = slots.map((s) => saveRow(data, s, false))
  const newest = newestValid(slots)
  const auto = newestValid(slots, AUTO_SLOTS)
  const p = Math.max(0, Math.min(1, state.assetsProgress))
  return {
    continueSlot: newest?.slot ?? null,
    continueLabel: newest === null ? 'Continue' : `Continue (${slotLabel(newest.slot)})`,
    continueDetail: newest === null ? 'No saved career yet' : summaryOf(data, newest),
    hasSaves: slots.some((s) => s.status !== 'empty'),
    rows,
    anyBad: rows.some((r) => r.error !== null),
    fallback: auto === null ? null : (rows.find((r) => r.slot === auto.slot) ?? null),
    assetsReady: state.assetsReady,
    assetsLabel: state.assetsReady ? 'Station models ready' : `Loading station models… ${Math.floor(p * 100)}%`,
    storageBanner: state.storage === 'localstorage' ? STORAGE_BANNER : null,
  }
}
